import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs/operators';
import { deepEqual } from '../../../../functions/deepEqual';
import { PageSelectorService } from './page-selector.service';
import { PageSelectorViewModel } from './page-selector.view-model';
import { CreateTrayScreenViewModel } from '../../../../../create-tray-screen/components/create-tray-screen/create-tray-screen.view-model';
import { CreateTrayScreenService } from '../../../../../create-tray-screen/components/create-tray-screen/create-tray-screen.service';
import { AlwaysError } from '../../../../../core/services/always-error.service';

@Component({
  selector: 'osn-cxb-page-selector',
  templateUrl: './page-selector.component.html',
  styleUrls: ['./page-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    PageSelectorService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PageSelectorComponent),
      multi: true,
    },
  ],
})
export class PageSelectorComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() public hintLabel: string;
  @Input() public errorLabel: string;
  @Input() public label: string;
  @Input() public placeholder = 'Source page';
  @Input() public includeGenrePages = false;
  @Input() public includeCollectionPages = false;
  @Input() public includeContentPages = true;
  @Input() public currentPageId: string;
  @Input() public excludeDrafts = true;

  public isReadonly = true;
  public inputFormControl: FormControl = new FormControl('');
  public selectFormControl: FormControl = new FormControl();
  public vm$ = this.pageSelectorService.vm$;
  public createTrayScreenServiceVM$: Observable<CreateTrayScreenViewModel> = this.createTrayScreenService.vm$;

  public isLoading$ = this.pageSelectorService.isLoading$.pipe(
    filter(() => !this.isReadonly),
    tap((isLoading) => this.setDisabledState(isLoading))
  );
  public items$ = this.vm$.pipe(
    map((vm) =>
      vm.filter((page) => {
        if (this.currentPageId && page.id === this.currentPageId) {
          return false;
        }

        if (page.pageType === 'genre' && this.includeGenrePages) {
          return true;
        }
        if (page.pageType === 'content' && this.includeContentPages) {
          return true;
        }
        if (page.pageType === 'collection' && this.includeCollectionPages) {
          return true;
        }
        return false;
      })
    )
  );

  private readonly destroyed$ = new Subject<void>();
  public propagateChange: Function = () => {};
  public propagateTouch: Function = () => {};
  public displayFn = (option: PageSelectorViewModel) => option.name;
  public compareFunction = (a, b) => a && b && a.id === b.id;

  constructor(
    private readonly createTrayScreenService: CreateTrayScreenService,
    private readonly pageSelectorService: PageSelectorService,
    private readonly cdr: ChangeDetectorRef,
    public readonly alwaysError: AlwaysError
  ) {}

  public ngOnInit(): void {
    this.pageSelectorService.fetchPages(this.excludeDrafts);
    this.inputFormControl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => this.pageSelectorService.search(value));

    this.selectFormControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        filter(() => this.selectFormControl.dirty),
        distinctUntilChanged((a, b) => deepEqual(a, b))
      )
      .subscribe((value) => this.propagateChange?.(value?.id));

    this.createTrayScreenServiceVM$.subscribe(({ readonly }) => {
      this.isReadonly = readonly;
    });
  }

  public ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  public writeValue(obj: string): void {
    this.pageSelectorService.prefillData$(obj).subscribe((value) => {
      if (value) {
        this.selectFormControl.setValue(value);
        this.cdr.detectChanges();
      }
    });
  }

  public setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.selectFormControl.disable();
    } else {
      this.selectFormControl.enable();
    }
  }
}
